import React, { createContext, useState, useEffect, useMemo } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

// MUI
import {
  ThemeProvider,
  CssBaseline,
  Box,
  Container
} from "@mui/material";
import { createTheme } from "@mui/material/styles";

import NavBar from "./components/NavBar";
import DetailedJobDescription from "./pages/DetailedJobDescription";
import TransitionWrapper from "./components/TransitionWrapper";
import Login from "./components/Login";
import { CategoryProvider } from "./contexts/CategoryContext";
import JobList from "./pages/JobList";
import FavoritesPage from "./pages/FavoritesPage";
import RecentPage from "./pages/RecentPage";
import CompaniesPage from "./pages/CompaniesPage";
import MobileBottomBar from "./components/MobileBottomBar";
import Profile from "./pages/Profile";
import EmployerDashboard from "./pages/EmployerDashboard";
import EmployerJobsPage from "./pages/EmployerJobsPage";
import EmployerJobDetail from "./pages/EmployerJobDetail";
import AdminPanel from "./pages/AdminPanel";
import { apiUrl } from "./config/api";
import useUmami from "./hooks/useUmami";

export const AuthContext = createContext();

// 🎨 THEME BUILDER
const getTheme = (mode) =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: "#6366f1",
      },
      secondary: {
        main: "#ec4899",
      },
      background: {
        default: mode === "dark" ? "#0f172a" : "#f1f5f9",
        paper: mode === "dark" ? "#1e293b" : "#ffffff",
      },
    },
    shape: {
      borderRadius: 18,
    },
    typography: {
      fontFamily: "Inter, sans-serif",
      h4: {
        fontWeight: 700,
      },
      h6: {
        fontWeight: 600,
      },
    },
  });

const App = () => {
  useUmami();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", variant: "" });

  const [mode, setMode] = useState(
    localStorage.getItem("themeMode") || "light"
  );

  const theme = useMemo(() => getTheme(mode), [mode]);

  const location = useLocation();

  useEffect(() => {
    const loggedIn = localStorage.getItem("isLoggedIn");
    if (loggedIn) setIsLoggedIn(true);
  }, []);

  const toggleTheme = () => {
    const newMode = mode === "light" ? "dark" : "light";
    setMode(newMode);
    localStorage.setItem("themeMode", newMode);
  };

  const handleShowLogin = () => setShowLogin(true);
  const handleCloseLogin = () => setShowLogin(false);

  const handleAuth = (type, data) => {
    const url =
      type === "register"
        ? apiUrl("register.php")
        : apiUrl("login.php");

    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          const user = data.user;

          localStorage.setItem("isLoggedIn", "true");
          localStorage.setItem("userId", user.id);
          localStorage.setItem("phone", user.phone);
          localStorage.setItem("firstName", user.firstName);
          localStorage.setItem("lastName", user.lastName);
          localStorage.setItem("email", user.email || "");
          localStorage.setItem("cvPath", user.cv_path || "");

          setIsLoggedIn(true);
          setShowLogin(false);
        } else {
          setAlert({ show: true, message: data.message, variant: "danger" });
        }
      })
      .catch(() => {
        setAlert({
          show: true,
          message: "Ошибка сервера",
          variant: "danger",
        });
      });
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsLoggedIn(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <AuthContext.Provider
        value={{
          isLoggedIn,
          handleShowLogin,
          handleLogout,
          toggleTheme,
          mode,
        }}
      >
        <CategoryProvider>

          <NavBar />

          {/* 🔥 ГЛОБАЛЬНЫЙ ФОН И КОНТЕНТ */}
          <Box
            sx={{
              minHeight: "100vh",
              background:
                mode === "dark"
                  ? `
                    radial-gradient(circle at 10% 20%, rgba(99,102,241,0.15), transparent 40%),
                    radial-gradient(circle at 90% 0%, rgba(236,72,153,0.15), transparent 40%),
                    #0f172a
                  `
                  : `
                    radial-gradient(circle at 10% 20%, rgba(99,102,241,0.15), transparent 40%),
                    radial-gradient(circle at 90% 0%, rgba(236,72,153,0.15), transparent 40%),
                    #f1f5f9
                  `,
              pt: { xs: 8, md: 10 }, // Padding top to account for fixed navbar
            }}
          >
            {/* 🔥 MAIN CONTENT */}
            <Container
              maxWidth="lg"
              sx={{
                pb: { xs: 12, md: 4 },
              }}
              className="transition-content"
            >
              <TransitionWrapper location={location}>
                <Routes location={location}>
                  <Route path="/" element={<JobList />} />
                  <Route path="/jobs" element={<JobList />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/job/:id" element={<DetailedJobDescription />} />
                  <Route path="/:lang/job/:id" element={<DetailedJobDescription />} />
                  <Route path="/favorites" element={<FavoritesPage />} />
                  <Route path="/recent" element={<RecentPage />} />
                  <Route path="/companies" element={<CompaniesPage />} />
            <Route path="/company/:companyName" element={<CompaniesPage />} />
                  {/* Новые маршруты для кабинета работодателя */}
                  <Route path="/employer/dashboard" element={<EmployerDashboard />} />
                  <Route path="/employer-jobs" element={<EmployerJobsPage />} />
                  <Route path="/employer-job/:id" element={<EmployerJobDetail />} />
                  <Route path="/admin/employers" element={<AdminPanel />} />
                </Routes>
              </TransitionWrapper>
            </Container>

            <MobileBottomBar />

            <Login
              show={showLogin}
              handleClose={handleCloseLogin}
              handleAuth={handleAuth}
              showAlert={alert.show}
              alert={alert}
            />

          </Box>

        </CategoryProvider>
      </AuthContext.Provider>
    </ThemeProvider>
  );
};

export default App;